import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Checkbox, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import hrmsService, { hrmsKeys, type WorkWeek } from '@/services/hrms.service';
import { WEEKDAY_OPTIONS, offDaysText, useWorkWeeks } from './useWorkWeeks';

const { Text } = Typography;

type Props = {
  open: boolean;
  /** Open straight into the "add" form instead of the list. */
  startAdding?: boolean;
  onClose: () => void;
  /** Called with the new code after a work week is added. */
  onCreated?: (code: string) => void;
};

/** Lists work weeks and lets HR add, rename or delete company-defined ones. */
const WorkWeekModal: React.FC<Props> = ({ open, startAdding, onClose, onCreated }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { workWeeks } = useWorkWeeks();
  const [editing, setEditing] = useState<WorkWeek | 'new' | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) setEditing(startAdding ? 'new' : null);
  }, [open, startAdding]);

  useEffect(() => {
    if (editing === 'new') {
      form.resetFields();
      form.setFieldsValue({ working_days: [1, 2, 3, 4, 5, 6] });
    } else if (editing) {
      form.setFieldsValue({
        code: editing.code,
        label: editing.label,
        working_days: [0, 1, 2, 3, 4, 5, 6].filter((d) => !editing.off_days.includes(d)),
      });
    }
  }, [editing, form]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: hrmsKeys.workWeeks() });

  const save = async () => {
    const v = await form.validateFields();
    const working = new Set<number>(v.working_days || []);
    const offDays = [0, 1, 2, 3, 4, 5, 6].filter((d) => !working.has(d));
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await hrmsService.createWorkWeek({
          code: v.code?.trim() || undefined,
          label: v.label?.trim() || undefined,
          off_days: offDays,
        });
        message.success(`Work week ${created.code} added`);
        await refresh();
        onCreated?.(created.code);
        if (startAdding) onClose();
        else setEditing(null);
      } else if (editing) {
        await hrmsService.updateWorkWeek(editing.code, { label: v.label?.trim() || '', off_days: offDays });
        message.success('Work week updated');
        await refresh();
        setEditing(null);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Could not save work week');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w: WorkWeek) => {
    try {
      await hrmsService.deleteWorkWeek(w.code);
      message.success(`Work week ${w.code} deleted`);
      await refresh();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not delete work week');
    }
  };

  const formView = (
    <Form form={form} layout="vertical">
      <Form.Item
        name="working_days"
        label="Working days"
        rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one working day' }]}
        help="Unticked days are the weekly off days. Timesheet auto-generation marks them as Weekly off."
      >
        <Checkbox.Group options={WEEKDAY_OPTIONS} />
      </Form.Item>
      <Form.Item name="label" label="Name" help="Leave blank to use the working days, e.g. Mon, Tue, … (off: Sun)">
        <Input maxLength={100} placeholder="e.g. Six-day week (Friday off)" />
      </Form.Item>
      <Form.Item
        name="code"
        label="Code"
        help="Stored on locations and in Excel imports. Leave blank to generate one (e.g. MON_SAT)."
        rules={[{ pattern: /^[A-Za-z0-9_ -]{2,30}$/, message: '2–30 letters, numbers or underscore' }]}
      >
        <Input disabled={editing !== 'new'} maxLength={30} placeholder="e.g. SAT_THU" />
      </Form.Item>
    </Form>
  );

  const listView = (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Built-in work weeks cannot be changed. Add your own for patterns such as a six-day week."
      />
      <Table<WorkWeek>
        rowKey="code"
        size="small"
        pagination={false}
        dataSource={workWeeks}
        columns={[
          { title: 'Code', dataIndex: 'code', width: 120 },
          {
            title: 'Name',
            dataIndex: 'label',
            render: (v: string, w) => (
              <Space size={4}>
                <Text>{v}</Text>
                {w.built_in && <Tag>Built-in</Tag>}
              </Space>
            ),
          },
          { title: 'Weekly off', key: 'off', width: 150, render: (_, w) => offDaysText(w.off_days) },
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, w) =>
              w.built_in ? null : (
                <Space size={0}>
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(w)} />
                  <Popconfirm title={`Delete work week ${w.code}?`} onConfirm={() => remove(w)}>
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </>
  );

  return (
    <Modal
      title={editing === 'new' ? 'Add work week' : editing ? `Edit work week ${editing.code}` : 'Work weeks'}
      open={open}
      width={editing ? 520 : 720}
      onCancel={() => (editing && !startAdding ? setEditing(null) : onClose())}
      destroyOnClose
      footer={
        editing ? (
          <Space>
            <Button onClick={() => (startAdding ? onClose() : setEditing(null))}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={save}>
              Save
            </Button>
          </Space>
        ) : (
          <Space>
            <Button icon={<PlusOutlined />} onClick={() => setEditing('new')}>
              Add work week
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Space>
        )
      }
    >
      {editing ? formView : listView}
    </Modal>
  );
};

export default WorkWeekModal;
